import { useEffect, useState } from 'react';
import { Form } from 'antd';
import dayjs from 'dayjs';
import type { LandlordPartnerRowDto } from '@evimo/client/models';
import { LandlordPaymentModel } from '@evimo/types/enums/landlord-payment-model';
import { SlaContractStatus } from '@evimo/types/enums/sla-contract-status';
import type { PartnerOrgSelection } from '~/app/landing/sla/shared/types';
import { PartnersService } from '~/app/services/partners/partners.service';
import type { LandlordPartnerFormValues } from '@/app/landing/sla/landlords/LandlordPartnerModal/types';

type Args = {
  open: boolean;
  organizationId: string;
  editingRow: LandlordPartnerRowDto | null;
  onSuccess: () => void;
};

export function useLandlordPartnerModal({
  open,
  organizationId,
  editingRow,
  onSuccess,
}: Args) {
  const [form] = Form.useForm<LandlordPartnerFormValues>();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPartner, setSelectedPartner] =
    useState<PartnerOrgSelection | null>(null);
  const [contractSnapshot, setContractSnapshot] =
    useState<LandlordPartnerFormValues | null>(null);

  const isEdit = editingRow != null;

  useEffect(() => {
    if (!open) {
      return;
    }
    setStep(0);
    setContractSnapshot(null);
    if (editingRow) {
      setSelectedPartner({
        id: editingRow.partnerOrgId,
        name: editingRow.partnerName,
      });
      form.setFieldsValue({
        responseDcDays: editingRow.slaContract?.responseDcDays,
        responseAcDays: editingRow.slaContract?.responseAcDays,
        uptimeTargetPct: editingRow.slaContract?.uptimeTargetPct,
        penaltyPerDayAmount: editingRow.slaContract?.penaltyPerDayAmount,
        penaltyCurrency: editingRow.slaContract?.penaltyCurrency ?? 'EUR',
        paymentModel: editingRow.slaContract?.paymentModel,
        validFrom: editingRow.slaContract?.validFrom
          ? dayjs(editingRow.slaContract.validFrom)
          : dayjs(),
        validTo: editingRow.slaContract?.validTo
          ? dayjs(editingRow.slaContract.validTo)
          : null,
        status: editingRow.slaContract?.status ?? SlaContractStatus.DRAFT,
      });
    } else {
      form.resetFields();
      setSelectedPartner(null);
      form.setFieldsValue({
        validFrom: dayjs(),
        status: SlaContractStatus.ACTIVE,
        paymentModel: LandlordPaymentModel.ANNUAL,
        penaltyCurrency: 'EUR',
      });
    }
  }, [open, editingRow, form]);

  const handleSubmit = async () => {
    const values =
      !isEdit && contractSnapshot
        ? contractSnapshot
        : await form.validateFields();

    setSubmitting(true);
    try {
      const payload = {
        responseDcDays: values.responseDcDays,
        responseAcDays: values.responseAcDays,
        uptimeTargetPct: values.uptimeTargetPct ?? null,
        penaltyPerDayAmount: values.penaltyPerDayAmount,
        penaltyCurrency: values.penaltyCurrency,
        paymentModel: values.paymentModel,
        validFrom: values.validFrom.toDate(),
        validTo: values.validTo ? values.validTo.toDate() : null,
        status: values.status,
      };

      if (isEdit && editingRow) {
        await PartnersService.updateLandlord(
          organizationId,
          editingRow.relationshipId,
          payload,
        );
      } else {
        await PartnersService.createLandlord(organizationId, {
          ...payload,
          partnerOrgId: selectedPartner?.id,
          partnerName: selectedPartner?.id ? undefined : selectedPartner?.name,
        });
      }
      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (step === 2) {
      try {
        const values = await form.validateFields();
        setContractSnapshot(values);
      } catch {
        return;
      }
    }
    setStep((current) => current + 1);
  };

  const canProceedFromPartnerStep = Boolean(
    selectedPartner?.name && !selectedPartner.alreadyLinked,
  );

  return {
    form,
    step,
    setStep,
    submitting,
    selectedPartner,
    setSelectedPartner,
    contractSnapshot,
    isEdit,
    handleSubmit,
    handleNext,
    canProceedFromPartnerStep,
  };
}
